// Relative imports, not the '@' alias: jest has no moduleNameMapper for '@'
// and jest.config.js may not be touched. See useCourseList.ts.
import ChessRestClient from '../../Api/ChessRestClient';
import {getLocalStorage} from '../../Utils/CommonUtils';
import {useQuery, UseQueryResult} from '@tanstack/react-query';

async function authHeader() {
  const token = await getLocalStorage('access_token');
  return {Authorization: `Bearer ${token}`};
}

/** A course already generated from a recording, if there is one. */
export type ExistingCourse = {
  status: 'pending' | 'running' | 'done' | 'failed';
  book_uuid: string | null;
  chapter_count: number;
};

export type Masterclass = {
  video_course_id: number;
  title: string;
  class_name: string;
  coach: string;
  category: string;
  /** ISO date, or null when the recording carries no date at all. */
  date: string | null;
  /**
   * False when `date` is the import date rather than the day the class was
   * held. Shown as approximate, so nobody filters to the wrong month and
   * concludes a recording is missing.
   */
  date_is_class_date: boolean;
  duration_min: number;
  is_private: boolean;
  has_transcript: boolean;
  course: ExistingCourse | null;
};

export type MonthOption = {value: string; label: string};

export type MasterclassPage = {
  months: MonthOption[];
  month: string;
  page: number;
  page_size: number;
  total: number;
  results: Masterclass[];
};

export type MasterclassQuery = {
  month?: string;
  q?: string;
  page?: number;
  pageSize?: number;
};

export async function fetchMasterclasses(
  query: MasterclassQuery = {},
): Promise<MasterclassPage> {
  const response = await ChessRestClient.get('/video_learning/masterclasses/', {
    headers: await authHeader(),
    params: {
      month: query.month,
      q: query.q || undefined,
      page: query.page,
      page_size: query.pageSize,
    },
  });
  return response.data as MasterclassPage;
}

/**
 * The recordings available to build a course from.
 *
 * `keepPreviousData` so paging or retyping a search does not blank the list
 * back to a spinner on every keystroke — the previous page stays legible
 * while the next one loads, which is the difference between a filter that
 * feels instant and one that flickers.
 */
export function useMasterclasses(
  query: MasterclassQuery,
  enabled = true,
): UseQueryResult<MasterclassPage, Error> {
  return useQuery<MasterclassPage, Error>(
    ['masterclass-catalogue', query.month, query.q, query.page, query.pageSize],
    () => fetchMasterclasses(query),
    {enabled, keepPreviousData: true, staleTime: 60 * 1000},
  );
}

/** "3 Sep 2026", or "—" when the recording carries no date. */
export function formatWhen(item: Masterclass): string {
  if (!item.date) return '—';
  const parsed = new Date(`${item.date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return item.date;
  return parsed.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** What the row should say about an existing course, or '' for none. */
export function courseNote(item: Masterclass): string {
  if (!item.course) return '';
  switch (item.course.status) {
    case 'done':
      return `Course already built · ${item.course.chapter_count} chapters`;
    case 'failed':
      return 'A previous build failed';
    default:
      return 'A build is already running';
  }
}

/**
 * Whether picking this recording would be refused.
 *
 * Only the first selected recording anchors the course, so a recording that
 * already has one can still be used as a later part. The picker uses this to
 * warn rather than to disable — the server is the authority, and it answers
 * with a 409 that offers a rebuild.
 */
export function wouldConflictAsFirst(item: Masterclass): boolean {
  return item.course != null;
}
